import { fetchQuery, graphql } from 'react-relay';

import { DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES } from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import logger from 'src/util/logger';

/**
 * GraphQL query for fetching property users on demand.
 * This query is only executed when the user clicks the DataPack download button,
 * rather than on initial page load.
 */
const propertyUsersQuery = graphql`
  query propertyUsersQueryQuery($id: ID!) {
    property(id: $id) {
      propertyUsers(first: 500) {
        edges {
          node {
            id
            externalIdentifier
            active { start finish }
            user {
              id
              externalIdentifier
              email
              givenName
              familyName
              active { start finish }
            }
          }
        }
      }
    }
  }
`;

/**
 * Fetches property users data on demand (lazy loading).
 * Used when generating the DataPack JSON download to avoid fetching
 * user data on every page load.
 * @param {object} environment - Relay environment
 * @param {string} propertyId - Property ID
 * @returns {Promise<object>} - Promise resolving to propertyUsers connection object
 */
export const fetchPropertyUsers = (environment, propertyId) => {
  if (!environment || !propertyId) {
    return Promise.reject(new Error('Missing environment or propertyUuid'));
  }

  return queryWithRetry({
    queryFn: () => {
      let resolvePromise;
      let rejectPromise;

      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const subscription = fetchQuery(
        environment,
        propertyUsersQuery,
        { id: propertyId },
      ).subscribe({
        next: (data) => {
          logger.log('Fetched property users for DataPack download');
          resolvePromise(data?.property?.propertyUsers);
        },
        error: (error) => {
          rejectPromise(error);
        },
      });

      // Attach unsubscribe method to the promise so queryWithRetry can cancel on timeout
      promise.unsubscribe = () => subscription.unsubscribe();

      return promise;
    },
    timeoutMs: DEFAULT_FETCH_QUERY_TIMEOUT,
    operationName: `Fetching property users for ${propertyId}`,
    maxRetries: DEFAULT_MAX_RETRIES,
  });
};

export default propertyUsersQuery;
